import { DataScopesEnum, LookupDataEnums, OrganizationTypesEnum } from '../../utilities/Constants';
import {
    IDataScopeLookupModel,
    ILookupModel,
    IMultiSelectOption,
    IValidRolesLookupModel,
} from '../../interfaces/ILookup';
import {
    IOrganizationBaseModel,
    IOrganizationDependencyModel,
    IOrganizationModel,
} from '../../interfaces/IOrganization';
import { IOrganizationRoleModel, IUserRightModel } from '../../interfaces/IUser';

import CheckmateSelect from '../shared/CheckmateSelect';
import CheckmateSelectHelper from '../../utilities/CheckmateSelectHelper';
import { IValidation } from '../../interfaces/IError';
import { Modal } from 'react-bootstrap';
import { SelectedRoleRightsDialog } from './SelectedRoleRightsDialog';
import UIHelper from '../../utilities/UIHelper';
import { cloneDeep } from 'lodash';
import { useGetDataScopes } from '../../shared/react-query-hooks/useGetDataScopes';
import { useGetLookupData } from '../../shared/react-query-hooks/useGetLookupData';
import { useState } from 'react';

const sortByLabel = (a: IMultiSelectOption, b: IMultiSelectOption) =>
    a.label < b.label ? -1 : 1;

interface IOrganizationRoleModalProps {
    currentOrganizationRole?: IOrganizationRoleModel;
    onCancel: () => void;
    onSave: (organizationRole: IOrganizationRoleModel) => void;
    homeOrganization: IOrganizationBaseModel;
    isLoadingZones?: boolean;
    isNew?: boolean;
    open: boolean;
    zones?: IOrganizationModel[];
    zonesCurrentlyAssigned?: IOrganizationModel[];
}

export const OrganizationRoleModal = (props: IOrganizationRoleModalProps) => {
    const [localOrganizationRole, setLocalOrganizationRole] = useState<
        IOrganizationRoleModel | undefined
    >( !props.isNew ? props.currentOrganizationRole : undefined);

    const [validation, setValidation] = useState<IValidation>({});
    const [selectedRights, setSelectedRights] = useState<IMultiSelectOption[]>(
        props.currentOrganizationRole?.rights
            ?.filter((right) => right.userOrganizationRight)
            .map(
                (right) =>
                    ({
                        id: right.id,
                        label: right.displayName ?? right.name ?? '',
                        value: right.id.toString(),
                    } as IMultiSelectOption)
            ) ?? []
    );
    const [showSelectedRoleRightsDialog, setShowSelectedRoleRightsDialog] =
        useState<boolean>(false);

    const dataScopesApi = useGetDataScopes();
    const userRightsApi = useGetLookupData(LookupDataEnums.UserRights);

    const resetDialog = () => {
        setLocalOrganizationRole(undefined);
        setValidation({});
        setSelectedRights([]);
        setShowSelectedRoleRightsDialog(false);
    };

    const getDefaultDataScopeForZone = (organization: IOrganizationModel) => {
        if (organization.dependentOrganizations) {
            const match = organization.dependentOrganizations?.find(
                (x: IOrganizationDependencyModel) => x.guid === props.homeOrganization.guid
            );
            let dataScopeId = 0;
            if (match && match.organizationType) {
                switch (match.organizationType.id) {
                    case OrganizationTypesEnum.Firm.Value:
                        dataScopeId =
                            match.priority === 1
                                ? DataScopesEnum.National.Value
                                : DataScopesEnum.LocalBasic.Value;
                        break;
                    case OrganizationTypesEnum.Company.Value:
                        dataScopeId = DataScopesEnum.Company.Value;
                        break;
                    case OrganizationTypesEnum.Insurer.Value:
                        dataScopeId = DataScopesEnum.Insurer.Value;
                        break;
                    default:
                }
            }

            return dataScopesApi.data?.find((x: ILookupModel) => x.id === dataScopeId);
        }
    };

    const getRolesBySelectedDataScope = () => {
        if (!localOrganizationRole?.dataScope?.id) return [];

        const found = dataScopesApi.data?.find((x) => x.id === localOrganizationRole.dataScope?.id);
        return found?.validRoles ?? [];
    };

    const handleCancelClick = () => {
        resetDialog();
        props.onCancel();
    };

    const handleDoneClick = () => {
        let valid = true;
        const validationCheck: IValidation = validation;
        if (
            !localOrganizationRole ||
            !localOrganizationRole.guid ||
            localOrganizationRole.guid == ''
        ) {
            valid = false;
            validationCheck.organization = ['Zone is required.'];
        }
        if (!localOrganizationRole?.dataScope) {
            valid = false;
            validationCheck.dataScope = ['Data Scope is required.'];
        }
        if (!localOrganizationRole?.role) {
            valid = false;
            validationCheck.role = ['Role is required.'];
        }

        setValidation(validationCheck);

        if (!valid) {
            return;
        }

        const currentRole = localOrganizationRole?.role;
        const defaultRights = currentRole?.rights;

        const mappedSelectedRights = selectedRights.map((selectedRight: IMultiSelectOption) => {
            const found = availableRights.find(
                (availableRight: ILookupModel) => availableRight.id === selectedRight.id
            );

            if (found) {
                const rightModel: IUserRightModel = {
                    id: found?.id,
                    guid: found?.guid,
                    userOrganizationRight: true,
                    displayName: found?.displayName,
                };

                return rightModel;
            }
        });

        const updatedOrganizationRole = {
            ...localOrganizationRole,
            rights: [...(defaultRights ?? []), ...mappedSelectedRights],
        } as IOrganizationRoleModel;

        props.onSave(updatedOrganizationRole);
    };

    const handleZoneChange = (selectedItem: any) => {
        if (selectedItem) {
            const organization = props.zones?.find((x) => x.guid === selectedItem.guid);
            if (organization) {
                const updatedOrganizationRole = {
                    ...organization,
                    dataScope: getDefaultDataScopeForZone(organization),
                };

                setValidation({ ...validation, organization: [''], dataScope: [''] });
                setLocalOrganizationRole(updatedOrganizationRole);
            }
        } else {
            setLocalOrganizationRole(undefined);
        }
    };

    const handleDataScopeChange = (dataScope: any) => {
        const lookupItem = UIHelper.multiSelectOptionTolookupModelMapper(dataScope);

        setLocalOrganizationRole({
            ...localOrganizationRole,
            dataScope: lookupItem,
            role: undefined,
        });

        if (dataScope !== null) {
            setValidation({ ...validation, dataScope: [''], role: ['Role is required.'] });
        } else {
            setValidation({
                ...validation,
                dataScope: ['Data Scope is required.'],
                role: ['Role is required.'],
            });
        }
    };

    const getFilteredRights = (updatedRole: IValidRolesLookupModel | undefined): IMultiSelectOption[] => {
        if (!updatedRole?.rights) return [];

        // collect all right IDs from the updated role
        const roleRightIds = updatedRole.rights.map((r) => r.id);

        // keep only selected rights not present in the updated role's rights
        const latestSelectedRights = selectedRights.filter((sr) => {
            const isNotInRole = roleRightIds.every((id) => id !== sr.id);
            return isNotInRole;
        });
        return latestSelectedRights;
    };

    const handleRoleChange = (selectedItem: any) => {
        const dataScopeId = localOrganizationRole?.dataScope?.id;
        const value = selectedItem ? selectedItem?.id : 0;
        if (value) {
            const updatedRole = cloneDeep(
                dataScopesApi.data
                    ?.find((x: IDataScopeLookupModel) => x.id === dataScopeId)!
                    .validRoles?.find((x) => x.id === parseInt(value))
            );
            setValidation({ ...validation, role: [''] });
            setLocalOrganizationRole({ ...localOrganizationRole, role: updatedRole });
            const filteredRights = getFilteredRights(updatedRole);
            setSelectedRights(filteredRights);
        } else {
            setValidation({ ...validation, role: ['Role is required.'] });
            setLocalOrganizationRole({ ...localOrganizationRole, role: undefined });
        }
    };

    const handleRightChange = (selectedItem: any) => {
        setSelectedRights(selectedItem);
    };

    const handleOpenSelectedRoleRightsDialog = () => {
        setShowSelectedRoleRightsDialog(true);
    };

    const handleCloseSelectedRoleRightsDialog = () => {
        setShowSelectedRoleRightsDialog(false);
    };

    const availableZones = props.isNew
        ? props.zones?.filter(
              (zone) => !props.zonesCurrentlyAssigned?.some((zca) => zca.guid === zone.guid)
          )
        : props.zones;

    const zoneOptions = CheckmateSelectHelper.getOrgOptions(availableZones ?? [], true);
    const dataScopeOptions = CheckmateSelectHelper.getLookupOptions(dataScopesApi.data ?? []);
    const roleOptions = CheckmateSelectHelper.getLookupOptions(getRolesBySelectedDataScope());

    const selectedDataScope = dataScopesApi.data?.find(
        (dataScope: IDataScopeLookupModel) => dataScope.id === localOrganizationRole?.dataScope?.id
    );
    const selectedRole = selectedDataScope?.validRoles?.find(
        (validRole: IValidRolesLookupModel) => validRole.id === localOrganizationRole?.role?.id
    );

    const availableRights = userRightsApi.data ?? [];
    const selectedRoleRights = selectedRole?.rights ?? [];

    const selectedRoleRightsOptions =
        availableRights
            .filter((d) => !selectedRoleRights.some((s) => s.id === d.id))
            .map(
                (availableRight: ILookupModel): IMultiSelectOption => ({
                    label: availableRight.displayName ?? availableRight.name ?? '',
                    value: availableRight.id.toString(),
                    id: availableRight.id,
                })
            ) ?? [];

    return (
        <>
            <Modal centered size="lg" show={props.open} backdrop>
                <Modal.Header>
                    <Modal.Title>Zone Access</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {zoneOptions && dataScopesApi.isSuccess && !props.isLoadingZones ? (
                        <>
                            <div className="row mb-2">
                                <label className="control-label col-sm-2" htmlFor="zone">
                                    Zone
                                </label>
                                <div className="col-sm-6 ">
                                    <CheckmateSelect
                                        isDisabled={!props.isNew}
                                        name="zone"
                                        onChange={handleZoneChange}
                                        options={zoneOptions.sort(sortByLabel)}
                                        value={zoneOptions?.find(
                                            (org) => org.guid === localOrganizationRole?.guid
                                        )}
                                    />
                                </div>
                                <div className="col-sm-4 text-danger">
                                    {validation.organization}
                                </div>
                            </div>
                            <div className="row mb-2">
                                <label className="control-label col-sm-2" htmlFor="dataScope">
                                    Data Scope
                                </label>
                                <div className="col-sm-6">
                                    <CheckmateSelect
                                        options={dataScopeOptions.sort(sortByLabel)}
                                        value={dataScopeOptions?.find(
                                            (org) => org.id === localOrganizationRole?.dataScope?.id
                                        )}
                                        name="dataScope"
                                        onChange={handleDataScopeChange}
                                    />
                                </div>
                                <div className="col-sm-4 text-danger">{validation.dataScope}</div>
                            </div>
                            {localOrganizationRole?.dataScope?.id && (
                                <div className="row mb-2">
                                    <label className="control-label col-sm-2" htmlFor="role">
                                        Role
                                    </label>
                                    <div className="col-sm-6">
                                        <CheckmateSelect
                                            options={roleOptions.sort(sortByLabel)}
                                            value={roleOptions?.find(
                                                (opt) => opt.id === localOrganizationRole?.role?.id
                                            )}
                                            name="role"
                                            onChange={(e: any) => {
                                                handleRoleChange(e);
                                            }}
                                        />
                                    </div>
                                    <div className="col-sm-4 text-danger">
                                        {localOrganizationRole?.role?.id && (
                                            <button
                                                className="btn btn-no-bg"
                                                onClick={handleOpenSelectedRoleRightsDialog}
                                            >
                                                <i className="fal fa-lg fa-check-circle color-gray" />
                                            </button>
                                        )}
                                        {validation.role}
                                    </div>
                                </div>
                            )}
                            {localOrganizationRole?.role?.id && userRightsApi.isSuccess && (
                                <div className="row mb-2">
                                    <label className="control-label col-sm-2" htmlFor="role">
                                        Rights
                                    </label>
                                    <div className="col-sm-6">
                                        <CheckmateSelect
                                            options={selectedRoleRightsOptions.sort(sortByLabel)}
                                            value={selectedRights}
                                            name="right"
                                            onChange={handleRightChange}
                                            isMulti={true}
                                        />
                                    </div>
                                    <div className="col-sm-4 text-danger">{validation?.rights}</div>
                                </div>
                            )}
                        </>
                    ) : (
                        <>Loading Zones...</>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <button className="btn btn-black" onClick={handleDoneClick}>
                        OK
                    </button>
                    <button className="btn btn-default" onClick={handleCancelClick}>
                        Cancel
                    </button>
                </Modal.Footer>
            </Modal>

            <SelectedRoleRightsDialog
                open={showSelectedRoleRightsDialog}
                title={`${localOrganizationRole?.role?.name} Rights`}
                selectedRoleRights={selectedRoleRights}
                onClose={handleCloseSelectedRoleRightsDialog}
            />
        </>
    );
};
